import * as React from 'react';
import { useState, useRef } from 'react';
import {
  EnergyUseReadDTO,
  EnergyUseCreateDTO,
  EnergyUseSettingsReadDTO,
  EnergyUseSettingsCreateDTO,
} from '../generated/api-client';
import {
  CrudConfig,
  CrudField,
  CrudDataGrid,
  CrudDataGridHandle,
} from '../components/CrudDataGrid';
import { GridRowModel } from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { PageLayout } from '../components/layout';
import { ActionButton } from '../components/common';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import { apiClient } from '../services/apiClient';

export const EnergyUsePage: React.FC = () => {
  const queryClient = useQueryClient();
  const crudGridRef = useRef<CrudDataGridHandle>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsFormData, setSettingsFormData] = useState<EnergyUseSettingsCreateDTO | null>(null);

  // Fetch settings
  const { data: settings, isLoading: isLoadingSettings } = useQuery(
    'energyUseSettings',
    async () => {
      const all = await apiClient.list_items_energy_use_settings__get();
      return all.length > 0 ? all[0] : null;
    }
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    async (data: EnergyUseSettingsCreateDTO) => {
      if (!settings?.id) {
        throw new Error('Settings not found');
      }
      return await apiClient.update_item_energy_use_settings__item_id__put(settings.id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('energyUseSettings');
        toast.success('Impostazioni aggiornate con successo');
        setSettingsDialogOpen(false);
      },
      onError: (error: any) => {
        if (Array.isArray(error?.detail)) {
          for (const detail of error.detail) {
            toast.error(`${detail.msg} (${detail.loc[1]})`);
          }
        } else {
          toast.error('Aggiornamento delle impostazioni non riuscito');
        }
      },
    }
  );

  const handleOpenSettings = () => {
    if (settings) {
      setSettingsFormData({
        default_energy_relevance_lower_limit: settings.default_energy_relevance_lower_limit,
        default_energy_relevance_upper_limit: settings.default_energy_relevance_upper_limit,
        weight_energy_relevance: settings.weight_energy_relevance,
        weight_technological_gap_vs_bats: settings.weight_technological_gap_vs_bats,
        weight_plant_management: settings.weight_plant_management,
        knowledge_measurement: settings.knowledge_measurement,
        minimum_threshold: settings.minimum_threshold,
      } as any);
      setSettingsDialogOpen(true);
    }
  };

  const handleCloseSettings = () => {
    setSettingsDialogOpen(false);
    setSettingsFormData(null);
  };

  const handleSaveSettings = () => {
    if (settingsFormData) {
      updateSettingsMutation.mutate(settingsFormData);
    }
  };

  const fields: CrudField[] = [
    {
      field: 'code',
      headerName: 'Codice',
      width: 200,
      editable: true,
    },
    {
      field: 'description',
      headerName: 'Descrizione',
      width: 1000,
      editable: true,
    },
    {
      field: 'is_default',
      headerName: 'Default',
      width: 100,
      editable: true,
      type: 'boolean',
      align: 'right',
      headerAlign: 'right',
    },
  ];

  const config: CrudConfig<EnergyUseReadDTO, EnergyUseCreateDTO> = {
    queryKey: 'energyUse',
    fields,
    listFn: async () => {
      return await apiClient.list_items_energy_use__get();
    },
    createFn: async (data: EnergyUseCreateDTO) => {
      return await apiClient.create_item_energy_use__post(data);
    },
    updateFn: async (id: string, data: EnergyUseCreateDTO) => {
      return await apiClient.update_item_energy_use__item_id__put(id, data);
    },
    deleteFn: async (id: string) => {
      await apiClient.delete_item_energy_use__item_id__delete(id);
    },
    getId: (row: EnergyUseReadDTO) => row.id || '',
    getCreateDto: (row: GridRowModel) => {
      return new EnergyUseCreateDTO({
        code: row.code || '',
        description: row.description || undefined,
        is_default: row.is_default ?? false,
      });
    },
  };

  return (
    <PageLayout
      header={
        <>
          <ElectricalServicesIcon fontSize="large" />
          <Typography variant="h5" sx={{ marginLeft: '1rem' }}>
            Usi energetici
          </Typography>
        </>
      }
      actions={
        <>
          <ActionButton
            icon={<AddIcon />}
            tooltip="Aggiungi nuovo uso energetico"
            onClick={() => crudGridRef.current?.addRow()}
          />
          <ActionButton
            icon={<SettingsIcon />}
            tooltip="Impostazioni"
            onClick={handleOpenSettings}
            disabled={!settings || isLoadingSettings}
          />
        </>
      }
    >
      <Box sx={{ p: 2 }}>
        <CrudDataGrid ref={crudGridRef} key="energy-use-grid" config={config} />
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={handleCloseSettings} fullWidth maxWidth="md">
        <DialogTitle>Impostazioni usi energetici</DialogTitle>
        <DialogContent>
          {settingsFormData && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Default Rilevanza Energetica Limite Inferiore"
                  type="number"
                  value={settingsFormData.default_energy_relevance_lower_limit ?? ''}
                  onChange={e =>
                    setSettingsFormData({
                      ...settingsFormData,
                      default_energy_relevance_lower_limit: parseInt(e.target.value) || undefined,
                    } as any)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Default Rilevanza Energetica Limite Superiore"
                  type="number"
                  value={settingsFormData.default_energy_relevance_upper_limit ?? ''}
                  onChange={e =>
                    setSettingsFormData({
                      ...settingsFormData,
                      default_energy_relevance_upper_limit: parseInt(e.target.value) || undefined,
                    } as any)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Peso Rilevanza Energetica"
                  type="number"
                  value={settingsFormData.weight_energy_relevance ?? ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Peso Gap Tecnologico vs BATs"
                  type="number"
                  value={settingsFormData.weight_technological_gap_vs_bats ?? ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Peso Gestione Impianti"
                  type="number"
                  value={settingsFormData.weight_plant_management ?? ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Conoscenza / Misurazione"
                  type="number"
                  value={settingsFormData.knowledge_measurement ?? ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Soglia Minima"
                  type="number"
                  value={settingsFormData.minimum_threshold ?? ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>Annulla</Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={updateSettingsMutation.isLoading}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};
